import React, {
  ReactElement,
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect
} from 'react';
import {
  Text,
  Textarea as ChakraTextarea,
  TextareaProps as ChakraTextareaProps,
  Flex,
  HStack
} from '@chakra-ui/react';
import { styles } from './textarea.styles';

enum TextareaSize {
  XS = "xs",
  SM = "sm",
  MD = "md",
  LG = "lg"
}

enum TextareaVariant {
  OUTLINE = "outline",
  FLUSHED = "flushed",
  FILLED = "filled",
  UNSTYLED = "unstyled"
}

export type TextareaProps = {
  size?: TextareaSize;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  variant?: TextareaVariant;
  className?: string;
  leftIcon?: ReactElement;
  rightIcon?: ReactElement;
  placeholder?: string;
  value?: string;
  maxLength?: number;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
} & ChakraTextareaProps;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      leftIcon,
      onChange,
      placeholder,
      readonly,
      required,
      rightIcon,
      value,
      className = '',
      disabled = false,
      maxLength = 2048,
      size = TextareaSize.MD,
      variant = TextareaVariant.OUTLINE,
      ...props
    },
    ref
  ) => {
    const localRef = useRef<HTMLTextAreaElement>(null);
    const [textLength, setTextLength] = React.useState(value?.length ?? 0);

    // Expose the local ref and reset function to parent
   useImperativeHandle(ref, () => {
  const element = localRef.current as HTMLTextAreaElement;
  (element as any).resetTextLength = () => setTextLength(0);
  return element; // return the real DOM node itself
});


    useEffect(() => {
      if (localRef.current) {
        localRef.current.focus();
        localRef.current.selectionStart = localRef.current.value.length;
        localRef.current.selectionEnd = localRef.current.value.length;
      }
    }, []);

    return (
      <HStack>
        <div className="left-icon">{leftIcon}</div>
        <Flex sx={styles.textarea}>
          <ChakraTextarea
            {...props}
            size={size}
            isDisabled={disabled}
            isReadOnly={readonly}
            isRequired={required}
            variant={variant}
            className={className}
            placeholder={placeholder}
            value={value}
            maxLength={maxLength}
            ref={localRef}
            onChange={e => {
              setTextLength(e.target.value.length);
              onChange?.(e);
            }}
          />
          <Text sx={styles.lengthIndicator}>
            {textLength}/{maxLength}
          </Text>
        </Flex>
        <div className="right-icon">{rightIcon}</div>
      </HStack>
    );
  }
);

Textarea.displayName = 'Textarea';
